import { useGT } from 'gt-react';
import { mergeOperatorEvidence } from './operatorEvidence';
import { operatorActionTitle } from './operatorActionTitle';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import {
  useGetAgentRunTimeline,
  AGENT_RUN_TIMELINE_POLL_MS,
} from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  buildSpans,
  aggregateTokens,
  coverChildren,
  sortSpanTreeByStart,
} from './utils';
import type { Span } from './types';

/** What `getRunTimeline` returns — the shape `useGetAgentRunTimeline` resolves to. */
export type RunTimeline = NonNullable<
  ReturnType<typeof useGetAgentRunTimeline>['data']
>;

// Walk the span tree collecting the child agent-run ids referenced by
// task-loop phase spans (deduped, preserving first-seen order).
function collectChildAgentRunIds(spans: Span[]): string[] {
  const ids: string[] = [];
  function walk(list: Span[]): void {
    for (const s of list) {
      if (s.childAgentRunId) ids.push(s.childAgentRunId);
      if (s.children) walk(s.children);
    }
  }
  walk(spans);
  return [...new Set(ids)];
}

/**
 * Nest each fetched child-run timeline beneath the task-loop phase span that spawned it.
 *
 * Pure so the assembled tree can be tested poll by poll: the hook calls it with whatever child
 * timelines have loaded so far, and every poll rebuilds the tree from scratch.
 */
export function attachChildRunTimelines(
  baseSpans: Span[],
  timelines: Readonly<Record<string, RunTimeline>>,
  rootRunStart: string,
): Span[] {
  function attach(list: Span[]): Span[] {
    return list.map((span): Span => {
      const timeline = span.childAgentRunId
        ? timelines[span.childAgentRunId]
        : undefined;
      if (timeline) {
        return {
          ...span,
          children: buildSpans(
            timeline.messages,
            timeline.toolCalls,
            // Every row in the assembled tree uses the root run's clock.
            // Child-relative offsets would pull later tasks back toward zero
            // when coverChildren expands their time windows.
            rootRunStart,
            timeline.run.model,
            timeline.workflowPhaseSteps,
          ),
        };
      }
      return span.children
        ? { ...span, children: attach(span.children) }
        : span;
    });
  }
  // Re-run aggregation now that child run timelines are attached: buildSpans
  // already aggregated baseSpans, but the phase/task spans had no children at
  // that point, so their rolled tokens/duration/model would otherwise stay
  // empty. aggregateTokens/coverChildren are idempotent, so this is safe.
  const attached = attach(baseSpans);
  attached.forEach(coverChildren);
  attached.forEach(aggregateTokens);
  return attached;
}

/**
 * A run's span tree, with the timelines of any child agent runs fetched and nested beneath the
 * task-loop phase spans that spawned them.
 *
 * `getRunTimeline` is single-run: its `toolCalls` cover only the run asked for, and a task-loop
 * run's real work lives on the child run each phase spawns (`ToolCallRunResponse.childAgentRunId`,
 * the `AgentRun.toolRunId` backlink). So the subtree is assembled client-side, one extra timeline
 * request per child run.
 *
 * Shared by the Background Agents trace table and the Agent Monitor's trajectory rail so the two
 * surfaces can't drift on how a run's tree is built.
 */
export function useRunSpans(
  agentRunId?: string,
  options: { includeOperatorSteps?: boolean } = {},
): {
  spans: Span[];
  timeline: RunTimeline | undefined;
  isLoading: boolean;
  isFetching: boolean;
  /**
   * When this run's timeline last came back from the server, as epoch ms (0 before the first
   * fetch). A pure, render-safe clock for "how long has this been going" — it advances on every
   * poll, so a caller gets a live elapsed without calling `Date.now()` during render.
   */
  dataUpdatedAt: number;
  refreshScreenshots: () => Promise<unknown>;
} {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } =
    useGetAgentRunTimeline(agentRunId, options);
  const gt = useGT();
  const isRunning = data?.run?.status === 'running';

  const baseSpans = useMemo(() => {
    if (!data) return [];
    return buildSpans(
      data.messages,
      data.toolCalls,
      data.run.startedAt,
      data.run.model,
      data.workflowPhaseSteps,
    );
  }, [data]);

  const childAgentRunIds = useMemo(
    () => collectChildAgentRunIds(baseSpans),
    [baseSpans],
  );
  const childIdsKey = childAgentRunIds.join(',');
  const { data: childTimelines, refetch: refetchChildren } = useQuery({
    queryKey: ['agent-child-timelines', childIdsKey],
    queryFn: async () => {
      const entries = await Promise.all(
        childAgentRunIds.map(async (id) => {
          try {
            const timeline = await rpcClient.agentDefinitions.getRunTimeline({
              agentRunId: id,
            });
            return [id, timeline] as const;
          } catch {
            // One unreadable child run must not break nesting for the rest
            return null;
          }
        }),
      );
      return Object.fromEntries(entries.filter((e) => e !== null));
    },
    enabled: childAgentRunIds.length > 0,
    staleTime: 30_000,
    // While the parent run is live the children are still accumulating steps, so match the
    // parent timeline's own cadence — otherwise `staleTime` would freeze the nested
    // subtrees at whatever they held when the phase span first appeared.
    refetchInterval: isRunning ? AGENT_RUN_TIMELINE_POLL_MS : false,
  });

  const runStartedAt = data?.run.startedAt;
  const spans = useMemo(
    () =>
      childTimelines && runStartedAt
        ? attachChildRunTimelines(baseSpans, childTimelines, runStartedAt)
        : baseSpans,
    [baseSpans, childTimelines, runStartedAt],
  );

  // Attaching a child run and then covering parents rewrites start times the same way
  // `buildSpans` does, and `mergeOperatorEvidence` only reorders the roots. Sort the whole tree
  // once more so a task that was pulled earlier by its children doesn't stay below an older sibling.
  const evidenceSpans = sortSpanTreeByStart(
    mergeOperatorEvidence(
      spans,
      data?.operatorSteps ?? [],
      new Date(data?.run.startedAt ?? 0).getTime(),
      (action) => operatorActionTitle(action, gt),
    ),
  );
  return {
    spans: evidenceSpans,
    timeline: data,
    isLoading,
    isFetching,
    dataUpdatedAt,
    refreshScreenshots: () => Promise.all([refetch(), refetchChildren()]),
  };
}
